"use client";

/**
 * PlanReportDisplay - Report planning result display
 */

import { useState } from "react";
import { colors, spacing, textStyles } from "@/ui/tokens";
import { CheckCircle, Warning, Lightbulb, ArrowForward } from "@mui/icons-material";
import {
  ToolCard,
  ToolHeader,
  ToolBody,
  ToolProgress,
  ToolError,
  ToolDivider,
  ToolSectionHeader,
} from "../../Tool";
import { deriveToolState } from "@/lib/sola/types/tools";
import type { ToolDisplayProps } from "@/lib/sola/types/tools";

interface PlanOutput {
  success: boolean;
  plan?: {
    title?: string;
    sections?: Array<{
      heading: string;
      talkingPoints?: string[];
    }>;
    gaps?: string[];
    nextActions?: string[];
  };
  error?: string;
}

export function PlanReportDisplay({ toolCall, isStreaming }: ToolDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const toolState = deriveToolState(toolCall);
  const { status, isLoading, isError, error } = toolState;

  const result = toolCall.finalOutput as PlanOutput | undefined;
  const plan = result?.plan;

  return (
    <ToolCard status={status} noPadding>
      <ToolHeader
        title={plan?.title || "Report Plan"}
        toolName={toolCall.toolName}
        status={status}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded((prev) => !prev)}
        icon={<Lightbulb style={{ fontSize: 18 }} />}
      />

      <ToolBody isOpen={isExpanded} noPadding>
        {isLoading && (
          <div style={{ padding: spacing[5], textAlign: "center" }}>
            <ToolProgress variant="dots" label="Planning report..." />
          </div>
        )}

        {isError && error && (
          <div style={{ padding: spacing[4] }}>
            <ToolError error={error} />
          </div>
        )}

        {!isLoading && plan && (
          <>
            {/* Sections */}
            {plan.sections && plan.sections.length > 0 && (
              <div
                style={{
                  paddingLeft: spacing[5],
                  paddingRight: spacing[5],
                  paddingTop: spacing[4],
                  paddingBottom: spacing[4],
                }}
              >
                <span
                  style={{
                    ...textStyles.body2,
                    fontWeight: 600,
                    display: "block",
                    marginBottom: spacing[4],
                  }}
                >
                  Sections
                </span>
                {plan.sections.map((section, i) => (
                  <div key={i} style={{ marginBottom: spacing[4] }}>
                    <span
                      style={{
                        ...textStyles.body2,
                        fontWeight: 600,
                        display: "block",
                        marginBottom: spacing[2],
                      }}
                    >
                      {section.heading}
                    </span>
                    {section.talkingPoints && (
                      <div>
                        {section.talkingPoints.map((point, j) => (
                          <div
                            key={j}
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: spacing[2],
                              paddingTop: 2,
                              paddingBottom: 2,
                            }}
                          >
                            <CheckCircle
                              style={{
                                fontSize: 16,
                                color: colors.signal.success,
                                flexShrink: 0,
                                marginTop: "2px",
                              }}
                            />
                            <span style={{ ...textStyles.body2 }}>
                              {point}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Gaps */}
            {plan.gaps && plan.gaps.length > 0 && (
              <>
                <ToolDivider />
                <div
                  style={{
                    paddingLeft: spacing[5],
                    paddingRight: spacing[5],
                    paddingTop: spacing[4],
                    paddingBottom: spacing[4],
                    backgroundColor: colors.signal.warningLight,
                  }}
                >
                  <ToolSectionHeader icon={<Warning />} label="Gaps to Address" compact iconColor={colors.signal.warning} />
                  <div>
                    {plan.gaps.map((gap, i) => (
                      <div
                        key={i}
                        style={{
                          paddingTop: 2,
                          paddingBottom: 2,
                        }}
                      >
                        <span style={{ ...textStyles.body2 }}>
                          {gap}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Next Actions */}
            {plan.nextActions && plan.nextActions.length > 0 && (
              <>
                <ToolDivider />
                <div
                  style={{
                    paddingLeft: spacing[5],
                    paddingRight: spacing[5],
                    paddingTop: spacing[4],
                    paddingBottom: spacing[4],
                    backgroundColor: colors.action.productLight,
                  }}
                >
                  <ToolSectionHeader icon={<Lightbulb />} label="Next Actions" compact />
                  <div>
                    {plan.nextActions.map((action, i) => (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: spacing[2],
                          paddingTop: 2,
                          paddingBottom: 2,
                        }}
                      >
                        <ArrowForward
                          style={{
                            fontSize: 16,
                            color: colors.action.product,
                            flexShrink: 0,
                            marginTop: "2px",
                          }}
                        />
                        <span
                          style={{
                            ...textStyles.body2,
                            color: colors.text.primary,
                          }}
                        >
                          {action}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </ToolBody>
    </ToolCard>
  );
}

export default PlanReportDisplay;
